import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin } from 'lucide-react';
import { LegalLinks } from './LegalLinks';

function SupportEmail({ testId }) {
  return <a data-testid={testId} href="mailto:lovepair369@gmail.com" className="text-[#AB3657] font-semibold break-all">lovepair369@gmail.com</a>;
}

function PolicyLayout({ title, children }) {
  const location = useLocation();
  const returnTo = /^\/(\?|$)/.test(location.state?.returnTo || '') ? location.state.returnTo : '/';
  return (
    <div className="bg-gradient-to-br from-[#FFF0F3] to-[#FFF8FB]"
      style={{ height: '100dvh', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}
      data-testid="policy-scroll-container">
      <header className="bg-white border-b border-[#FFE0E8] sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={returnTo} data-testid="policy-back-button" aria-label="Back to LovePair"
            className="w-10 h-10 shrink-0 rounded-xl bg-[#FFF0F3] flex items-center justify-center text-[#F4436C] active:scale-95 transition-transform">
            <ArrowLeft size={18} />
          </Link>
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-[#946F7D] uppercase">LovePair</p>
            <h1 data-testid="policy-title" className="text-lg font-bold text-[#4A2C35] leading-tight">{title}</h1>
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-6 pb-10 space-y-6" data-testid="policy-content">{children}</main>
      <footer className="border-t border-[#FFE0E8] bg-white py-5 px-4 text-center space-y-4" data-testid="policy-footer">
        <p className="text-xs text-[#8B6B75]">Questions? <SupportEmail testId="policy-support-email" /></p>
        <LegalLinks prefix="policy" />
        <Link to={returnTo} data-testid="policy-home-link" className="inline-block text-xs text-[#AB3657] font-semibold">Back to LovePair</Link>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return <section className="border-b border-[#EFD8E0] pb-6 last:border-0" data-testid={`policy-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <h2 className="text-base md:text-lg font-bold text-[#4A2C35] mb-3">{title}</h2>
    <div className="space-y-3 text-sm text-[#6B4C55] leading-relaxed break-words">{children}</div>
  </section>;
}

export function PrivacyPolicy() {
  return <PolicyLayout title="Privacy Policy">
    <Section title="Who We Are">
      <p>LovePair is a couple wellness and bonding platform operated by LovePair India.</p>
      <p>Contact: <SupportEmail testId="privacy-contact-email" /></p>
    </Section>
    <Section title="Information We Collect">
      <p><strong>Device and usage information:</strong> A browser fingerprint and local device identifier track free uses of Dice, Truth or Dare, and Intimacy. These records can be linked to the contact details you provide.</p>
      <p><strong>Email:</strong> Provided when saving or restoring progress, requesting support, or submitting a payment.</p>
      <p><strong>Payment details:</strong> For manual UPI verification, we store your email, phone number, order ID, 12-digit UTR, amount, payment status, and transaction timestamps. We may record the request IP address to help prevent abuse.</p>
      <p>Payments take place in your UPI app. We do not collect your UPI PIN, bank password, or card details.</p>
    </Section>
    <Section title="How We Use Information">
      <p>We use these records to provide the app, enforce free usage limits, verify payments, activate premium access, restore progress, and respond to support requests.</p>
      <p>Shop launch requests are sent through your own email app when you choose to contact us. We do not enrol you in marketing automatically.</p>
    </Section>
    <Section title="Storage and Sharing">
      <p>App records are stored in our cloud-hosted database. Uploaded images are held in cloud object storage. Infrastructure providers process this information to operate the service.</p>
      <p>We do not sell your personal information. Your bank and UPI provider handle the payment itself under their own privacy terms. We may disclose information if required by law.</p>
    </Section>
    <Section title="Local Storage">
      <p>We use browser storage for your device identifier, saved email, and unfinished payment reference. Removing browser data or uninstalling the app may remove those local records. Save Progress allows you to link your email before changing devices.</p>
    </Section>
    <Section title="Your Rights">
      <p>For access, correction, or deletion of your data, email <SupportEmail testId="privacy-rights-email" />. We aim to respond within 7 business days; records required for legal or payment-dispute purposes may need to be retained.</p>
    </Section>
    <Section title="Audience and Changes">
      <p>LovePair is intended for couples and partners. We do not knowingly collect personal data from minors. Contact us if you believe a minor has provided information.</p>
      <p>We may revise this policy when the service or our data practices change.</p>
    </Section>
  </PolicyLayout>;
}

export function TermsOfService() {
  return <PolicyLayout title="Terms of Service">
    <Section title="Acceptance of Terms">
      <p>By using LovePair, you agree to these terms. Activities are for consenting couples and partners; participation is always optional.</p>
    </Section>
    <Section title="The Service">
      <p>LovePair offers Dice challenges, Truth or Dare, couple games, and Intimacy activities for entertainment and relationship bonding. It is not a substitute for professional medical or relationship advice.</p>
      <p>The Shop collections are coming soon. Product previews are not items available for checkout.</p>
    </Section>
    <Section title="Free and Premium Access">
      <p>Free access includes <strong>10 Dice rolls, 10 Truth or Dare spins, and 5 Intimacy reveals</strong> per device.</p>
      <p><strong>LovePair Premium costs ₹69 for 60 days (approximately 2 months)</strong> of unlimited access to the premium features.</p>
      <p>Payment is a <strong>one-time manual UPI transfer</strong>. Submit your email, phone, and 12-digit UTR for review. Premium begins when our team approves the verified payment, not when a reference is submitted.</p>
      <p><strong>No automatic charges:</strong> Access expires after 60 days. Renew only by choosing to make another payment. There is no UPI mandate or automatic renewal in this payment flow.</p>
    </Section>
    <Section title="Your Responsibilities">
      <p>Provide accurate payment details, use the app lawfully and for personal purposes, respect your partner's boundaries, and do not attempt to bypass usage limits.</p>
      <p>Keep your order ID and UTR for support. Never share your UPI PIN or banking passwords with LovePair.</p>
    </Section>
    <Section title="Intellectual Property">
      <p>LovePair's original content, design, and functionality belong to LovePair India. Third-party materials remain the property of their respective owners. Unauthorised redistribution is prohibited.</p>
    </Section>
    <Section title="Availability and Liability">
      <p>The service is provided as available. Interruptions may occur. Nothing in these terms excludes rights or liabilities that cannot lawfully be excluded.</p>
    </Section>
    <Section title="Governing Law and Contact">
      <p>These terms are governed by Indian law, subject to applicable consumer protections.</p>
      <p>Questions: <SupportEmail testId="terms-contact-email" /></p>
    </Section>
  </PolicyLayout>;
}

export function RefundPolicy() {
  return <PolicyLayout title="Refund Policy">
    <Section title="Payment and Activation">
      <p><strong>₹69 buys 60 days of LovePair Premium.</strong> Payments are one-time UPI transfers reviewed manually after you submit your UTR. Submitting a UTR is not confirmation that payment has been received.</p>
      <p>Your access starts on approval. Check your payment status in the app; do not pay again while the original transaction is under review.</p>
    </Section>
    <Section title="Refunds for Digital Access">
      <p>Activated digital access is generally non-refundable, including unused days, except for the cases below or where a refund is required by applicable law.</p>
    </Section>
    <Section title="Exceptions">
      <p>Contact us about duplicate payments for the same purchase, unauthorised transactions, or payment deducted without access being activated within 24 hours.</p>
      <p>Please report issues within 48 hours where possible. Include your payment email, order ID, 12-digit UTR, payment date, and a description. We will review the transaction and communicate the outcome. This does not limit your statutory rights.</p>
      <p>Never send your UPI PIN, bank password, or full card information.</p>
    </Section>
    <Section title="Expiry and Cancellation">
      <p>Manual UPI payments do not renew automatically. There is no future debit to cancel. You can stop using the app at any time; your approved access remains valid until its expiry date.</p>
      <p>For a payment dispute or an account-deletion request, contact support.</p>
    </Section>
    <Section title="Request Support">
      <p>Email <SupportEmail testId="refund-contact-email" />. We typically respond within 2 business days. Approved refund timing depends on your bank and the payment method.</p>
    </Section>
  </PolicyLayout>;
}

export function ContactPage() {
  return <PolicyLayout title="Contact Us">
    <Section title="LovePair Support">
      <p>For app support, payment verification, refunds, privacy requests, or Shop enquiries:</p>
      <div className="flex items-center gap-3"><Mail size={20} className="shrink-0 text-[#F4436C]" /><SupportEmail testId="contact-email-link" /></div>
      <p data-testid="contact-response-time">We typically respond within 2 business days.</p>
    </Section>
    <Section title="Business Details">
      <p className="flex items-center gap-3" data-testid="contact-business"><MapPin size={20} className="shrink-0 text-[#F4436C]" />LovePair India · India</p>
      <p data-testid="contact-hours">Monday–Saturday, 10:00 AM–6:00 PM IST. Sunday: closed.</p>
    </Section>
    <Section title="Payment Support">
      <p>For payment issues, use the subject <strong>[URGENT] Payment Issue</strong> and include your order ID, 12-digit UTR, payment email, and transaction date. Do not include banking passwords or your UPI PIN.</p>
      <p>UPI payments are verified manually. Premium activates only after approval. If you have already submitted a reference, check its status before making another payment.</p>
    </Section>
    <Section title="Progress and Renewals">
      <p>Save Progress links your email so you can restore access on another device. For an existing paid subscription, use Restore with the email or phone provided at payment.</p>
      <p>Premium expires after 60 days. There are no automatic charges or renewal reminders in the current UPI flow.</p>
    </Section>
  </PolicyLayout>;
}